import { useEffect, useState } from 'react';
import { queryClient } from '@/common/contexts/react-query/ReactQueryProvider';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { useFeatures } from '@/common/integrations/feature-flag/feature-flag.hooks';
import {
  CardStatus,
  ICard,
  IMenhirCard,
  TNormalizedCardDetails,
} from '@/common/integrations/menhir/employee/card/card.types';
import { useGetFeatures } from '@/common/integrations/menhir/employee/features/features.hooks';
import { MENHIR_ENDPOINTS } from '@/common/integrations/menhir/menhir.endpoints';
import { getStorageItem } from '@/common/integrations/storage/storage.service';
import { IAddress } from '@/common/types/general';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { CardService, validateCardName } from './card.service';

export const useValidateCardName = (
  options?: UseMutationOptions<unknown, unknown, { cardHolderName: string }>
) => {
  return useMutation({
    mutationFn: ({ cardHolderName }) => validateCardName(cardHolderName),
    ...options,
  });
};

export function useGetCards(options?: UseQueryOptions<ICard[], any, ICard[]>) {
  const { data: cards, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.CARD.CARDS],
    queryFn: CardService.getEmployeeCards,
    ...options,
  });
  return { cards, ...rest };
}

export function useGetCardsData(
  options?: UseQueryOptions<ICard[], any, ICard[]>
) {
  const { cards, error, isInitialLoading, isLoading } = useGetCards(options);

  const digitalCard = cards?.find((card) => card.format === 'digital');
  const expiredCard = cards?.find((card) => card.status === 'expired');
  const cancelledCard = cards?.find((card) => card.status === 'cancelled');
  const expiringCard = cards?.find((card) => card.isExpiring);

  //Cards before digital cards initiative that are not active yet
  const inactiveCard = cards?.find((card) => card.status === 'inactive');

  const isPlasticRequested = Boolean(
    digitalCard && digitalCard.isPlasticRequested
  );

  return {
    cards,
    isInitialLoading,
    isLoading,
    error,
    inactiveCard,
    digitalCard,
    expiredCard,
    cancelledCard,
    expiringCard,
    isPlasticRequested,
  };
}

export function resetGetCardQueryData() {
  queryClient.resetQueries([MENHIR_ENDPOINTS.EMPLOYEE.CARD.CARDS]);
  queryClient.resetQueries([MENHIR_ENDPOINTS.EMPLOYEE.CARD.ROOT]);
}

export function useRequestCard(
  options?: UseMutationOptions<
    ICard,
    unknown,
    {
      address?: IAddress;
      cardHolderName?: string;
    }
  >
) {
  return useMutation({
    mutationFn: ({ address, cardHolderName }) =>
      CardService.requestCard({
        shippingAddress: address,
        cardHolderName,
      }),
    ...options,
  });
}

export function useRequestPlasticCard(
  options?: UseMutationOptions<
    ICard,
    unknown,
    {
      cardId: IMenhirCard['id'];
      address?: IAddress;
      cardHolderName?: string;
    }
  >
) {
  return useMutation({
    mutationFn: ({ cardId, address, cardHolderName }) =>
      CardService.requestPhysicalCard({
        cardId,
        shippingAddress: address,
        cardHolderName,
      }),
    ...options,
  });
}

export function useCancelCard(
  options?: UseMutationOptions<
    CardStatus,
    unknown,
    {
      cardId: IMenhirCard['id'];
    }
  >
) {
  return useMutation({
    mutationFn: ({ cardId }) => CardService.cancelCard(cardId),
    onSuccess: onStatusChange,
    ...options,
  });
}

export function useLockCard(
  options?: UseMutationOptions<
    CardStatus,
    unknown,
    {
      cardId: IMenhirCard['id'];
    }
  >
) {
  return useMutation({
    mutationFn: ({ cardId }) => CardService.lockCard(cardId),
    onSuccess: onStatusChange,
    ...options,
  });
}

export function useUnlockCard(
  options?: UseMutationOptions<
    CardStatus,
    unknown,
    {
      cardId: IMenhirCard['id'];
    }
  >
) {
  return useMutation({
    mutationFn: ({ cardId }) => CardService.unlockCard(cardId),
    onSuccess: onStatusChange,
    ...options,
  });
}

export function useActivateCard(
  options?: UseMutationOptions<
    CardStatus,
    unknown,
    {
      cardId: IMenhirCard['id'];
      panCode: string;
    }
  >
) {
  return useMutation({
    mutationFn: ({ cardId, panCode }) =>
      CardService.activateCard({ cardId, panConfirmation: panCode }),
    onSuccess: onStatusChange,
    ...options,
  });
}

export function useRequestCardPin(
  options?: UseMutationOptions<
    any,
    unknown,
    {
      cardId: IMenhirCard['id'];
    }
  >
) {
  return useMutation({
    mutationFn: ({ cardId }) => CardService.requestPin(cardId),
    ...options,
  });
}

export function useRequestPinToken(
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof CardService.requestPinToken>>,
    any
  >
) {
  return useMutation({
    mutationFn: CardService.requestPinToken,
    ...options,
  });
}

export function useResetPin(
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof CardService.resetPin>>,
    any,
    Parameters<typeof CardService.resetPin>[0]
  >
) {
  return useMutation({
    mutationFn: CardService.resetPin,
    ...options,
  });
}

export function useRequestCardDetails(
  options?: UseMutationOptions<
    TNormalizedCardDetails | undefined,
    unknown,
    { id: string; status: ICard['status']; requiresEncryption?: boolean }
  >
) {
  return useMutation({
    mutationFn: ({ id, status, requiresEncryption }) =>
      CardService.requestCardDetails(id, status, requiresEncryption),
    ...options,
  });
}

const onStatusChange = (newStatus: CardStatus) => {
  queryClient.invalidateQueries([MENHIR_ENDPOINTS.EMPLOYEE.CARD.CARDS]);
  queryClient.setQueryData<ICard | null>(
    [MENHIR_ENDPOINTS.EMPLOYEE.CARD.ROOT],
    (card) => {
      if (card) {
        return {
          ...card,
          status: newStatus,
        };
      }
    }
  );
};

export const useCanDisplayCardDetailsBanner = () => {
  const {
    features: { ff_digital_cards },
  } = useFeatures();

  const [dismissed, setDismissed] = useState(true);
  const { digitalCard, isPlasticRequested } = useGetCardsData();

  useEffect(() => {
    async function checkDismissed() {
      const dismissed = await getStorageItem(
        'cvrflex_flightdeck_check_digital_card_details_dismissed'
      );
      setDismissed(!!dismissed);
    }
    checkDismissed();
  }, []);

  return (
    ff_digital_cards &&
    (digitalCard && !digitalCard.isPlasticRequested
      ? !dismissed
      : isPlasticRequested)
  );
};

export const useShouldShowCardExpirationBanner = () => {
  const {
    isInitialLoading: loadingCards,
    error: getCardsError,
    cards,
    cancelledCard,
    expiringCard,
    expiredCard,
  } = useGetCardsData();

  if (loadingCards || getCardsError) {
    return {
      showExpirationBanner: false,
      isExpired: false,
    };
  }

  const hasSingleExpiringCard = Boolean(cards?.length === 1 && expiringCard);
  const hasSingleExpiredCard = Boolean(cards?.length === 1 && expiredCard);

  return {
    showExpirationBanner:
      !cancelledCard && (hasSingleExpiringCard || hasSingleExpiredCard),
    isExpired: hasSingleExpiredCard,
  };
};

export const useRequestDigitalCard = () => {
  const {
    features: { FF_CARD },
  } = useGetFeatures();

  const { company } = useEmployeeDetails();
  const requestCard = useRequestCard();

  return {
    requestDigitalCard: async () => {
      if (FF_CARD) {
        const cards = await CardService.getEmployeeCards();
        if (
          company.settings.cardRequestFormat === 'digital' &&
          !cards?.length
        ) {
          return requestCard.mutateAsync({});
        }
      }
      return Promise.resolve();
    },
  };
};
